import { useEffect, useRef, useMemo, useState } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage } from 'react-intl';
import { AGGClient } from 'src/clients/agg-client/agg-client';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { Tracking } from '@/analytics/atl-analytics/tracking';
import { useAnalyticsClient } from '@/analytics/wac-analytics/analytics-client';
import { useIsDesktop } from '@/components/user-context/helpers/cohorts';
import { Button } from '@/libraries/design/components/button/src/button';
import { LOCALES } from '@/libraries/web-framework/src/localization/localization.constants';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { ProductKeyTypes } from '@/libraries/wpl/foundations/src/data-event-props';
import { dynamicConfigManifest } from '@/utils/dynamic-configs/dynamic-config-manifest';
import { getDynamicConfig } from '@/utils/dynamic-configs/get-dynamic-config/get-dynamic-config';
import { experiments } from '@/utils/experiment/manifest';
import { useExperiment } from '@/utils/experiment/use-experiment';
import { useExperimentExposure } from '@/utils/experiment/use-experiment-exposure';
import { useLocation } from '@/utils/location-provider';
import { useLoggedInUser } from '@/utils/logged-in-user-provider';

import { getIsEnterpriseSalesLead, getIsSMBSalesLead } from './contact-sales-cta-helpers';

const fullWidthStyles = css({
	maxWidth: 'none',
	width: '100%',
});

const SALES_LEADS_ROUTES = ['/', '/software/confluence', '/software/jira'];

/**
 * The WAC homepage. `useLocation` can report the homepage as either '/' or an
 * empty string, which the surrounding nav code already defends against.
 */
const HOMEPAGE_ROUTES = ['/', ''];

/**
 * Which eligibility path put the CTA on screen. The two are mutually exclusive
 * (see `useAnyContactSalesCtaEligibility`), so exactly one applies per render.
 */
type SalesLeadSegment = 'smb' | 'enterprise';

/**
 * Both paths render the same button, on the same page, with the same label and
 * container — so without this the SMB and Enterprise events are
 * indistinguishable in the warehouse. That matters on '/' in particular, which
 * is the only route the Enterprise experiment runs on and is also an SMB route.
 *
 * The Statsig readout does not depend on this (exposure only fires for
 * Enterprise/Strategic, so SMB users are in neither arm), but any direct
 * `event_ui` query does — including the launch monitoring dashboard.
 *
 * `actionSubjectId` is deliberately NOT changed on the view event: that one
 * already ships as `contactSalesCta` and is a top-level column existing
 * dashboards filter on. The view event carries `leadSegment` instead.
 */
const CTA_CLICK_ACTION_SUBJECT_ID: Record<SalesLeadSegment, string> = {
	smb: 'contactSalesCtaSmb',
	enterprise: 'contactSalesCtaEnterprise',
};

const createSalesInboundAggClient = () =>
	new AGGClient({
		gatewayUrl:
			process.env['NODE_ENV'] === 'production'
				? 'https://www.atlassian.com/gateway/api/graphql'
				: 'https://wac.stg.internal.atlassian.com/gateway/api/graphql',
	});

const toNonLocalizedRoute = (pathname: string): string => {
	const locale = LOCALES.find(
		(candidate) => pathname === `/${candidate}` || pathname.startsWith(`/${candidate}/`),
	);
	const withoutLocale = locale ? pathname.slice(locale.length + 1) : pathname;
	return withoutLocale.replace(/\/+$/, '') || '/';
};

export const isSalesLeadsCtaRoute = (routes: string[], pathname: string): boolean =>
	routes.some((route) => toNonLocalizedRoute(route) === toNonLocalizedRoute(pathname));

export const useContactSalesCtaEligibility = (): {
	isEligible: boolean;
	isLoading: boolean;
} => {
	const { accountId } = useLoggedInUser();
	const { pathname } = useLocation();
	// the CTA is only rendered on the page paths listed in this dynamic config
	// https://switcheroo.atlassian.com/ui/dynamic-configs/key/wac-sales-leads-cta-route-list
	const routes = getDynamicConfig({
		config: dynamicConfigManifest.routesAllowingSalesLeadsCta,
		defaultValue: SALES_LEADS_ROUTES,
	});
	const isRouteAllowed = isSalesLeadsCtaRoute(routes, pathname);

	const [isSalesLead, setIsSalesLead] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	useEffect(() => {
		if (!isRouteAllowed || !accountId) return;
		setIsLoading(true);

		getIsSMBSalesLead({
			accountId,
			aggClient: createSalesInboundAggClient(),
		})
			.then((response) => {
				setIsSalesLead(!!response);
			})
			.catch(() => {
				console.error('Error fetching GUPS user profile for SMB Sales lead');
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [accountId, isRouteAllowed]);

	if (!accountId || !isRouteAllowed) {
		return { isEligible: false, isLoading: false };
	}

	return {
		isEligible: isSalesLead,
		isLoading,
	};
};

/**
 * Enterprise/Strategic eligibility for the global-nav "Contact us" CTA.
 * TWCG-2490 · Switcheroo FD-232121.
 *
 * Exposure timing is load-bearing and is the highest-risk detail in this
 * experiment:
 *
 *  1. BOTH arms run the GUPS account-segment lookup. The call in control is not
 *     waste — it *is* the eligibility gate that defines the comparable
 *     population. Gating the lookup on the cohort instead would leave control's
 *     Enterprise population unidentifiable, and the experiment unanalysable.
 *  2. The experiment is registered with `fireExperimentExposure: false`, so
 *     merely evaluating it enrols nobody.
 *  3. Exposure fires manually, and only once the lookup resolves to an eligible
 *     segment. Enrolling on homepage arrival instead would dilute enrolment
 *     from ~6,483 to ~71,358 users per 4 weeks (~11x) and collapse the MDE.
 *
 * Desktop only, per the P2c scope decision. The viewport guard lives here
 * rather than at the mount site because `mobile-nav-links-container.tsx`
 * renders the same CTA, so a mount-site-only guard would be easy to miss.
 */
export const useEnterpriseContactSalesCtaEligibility = (): {
	isEligible: boolean;
	isLoading: boolean;
} => {
	const { accountId } = useLoggedInUser();
	const { pathname } = useLocation();
	const isDesktop = useIsDesktop();

	// Exact homepage match, deliberately not locale-normalised: the audience
	// this experiment is powered against was measured on page_path = '/', so
	// localised homepages were never counted and must not be enrolled.
	const isHomepage = HOMEPAGE_ROUTES.includes(pathname);
	const shouldEvaluateSegment = isHomepage && !!isDesktop && !!accountId;

	const [isEnterpriseSalesLead, setIsEnterpriseSalesLead] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const experimentResult = useExperiment(experiments.twcg2490EnterpriseContactSalesCta);
	const [, fireExposure] = useExperimentExposure(experiments.twcg2490EnterpriseContactSalesCta);

	useEffect(() => {
		// `shouldEvaluateSegment` already includes `!!accountId`, and TypeScript
		// narrows `accountId` through it (control flow analysis of aliased
		// conditions), so no separate accountId guard is needed here.
		if (!shouldEvaluateSegment) {
			return;
		}

		setIsLoading(true);

		getIsEnterpriseSalesLead({
			accountId,
			aggClient: createSalesInboundAggClient(),
		})
			.then((response) => {
				setIsEnterpriseSalesLead(!!response);
			})
			.catch(() => {
				console.error('Error fetching GUPS account profile for Enterprise Sales lead');
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [accountId, shouldEvaluateSegment]);

	// Enrol only now that the user is known to be in the target segment. A GUPS
	// error, a timeout, or an absent/unrecognised segment must NOT enrol —
	// doing so inflates both denominators and biases the result toward null.
	//
	// The ref keeps this to exactly one exposure per mount. `fireExposure` is a
	// useCallback over the evaluated enrollment, so its identity changes on
	// every render; without the guard this effect would re-run and
	// `useExperimentExposure` would log an "already fired" warning each time.
	const hasFiredExposureRef = useRef(false);

	useEffect(() => {
		if (isEnterpriseSalesLead && !hasFiredExposureRef.current) {
			hasFiredExposureRef.current = true;
			fireExposure();
		}
	}, [isEnterpriseSalesLead, fireExposure]);

	if (!shouldEvaluateSegment) {
		return { isEligible: false, isLoading: false };
	}

	const isInTreatment = 'value' in experimentResult && experimentResult.value === true;

	return {
		isEligible: isEnterpriseSalesLead && isInTreatment,
		isLoading,
	};
};

/**
 * Combined eligibility across the shipped SMB path and the Enterprise
 * experiment. The two are mutually exclusive by construction —
 * `smbUserVisitedPricingPages` is SMB-only, confirmed with the GUPS team on
 * 2026-09-18 — so an Enterprise/Strategic account can never satisfy both, and
 * the control baseline for the Enterprise experiment is genuinely zero.
 */
export const useAnyContactSalesCtaEligibility = (): {
	isEligible: boolean;
	isLoading: boolean;
	leadSegment: SalesLeadSegment | undefined;
} => {
	const smb = useContactSalesCtaEligibility();
	const enterprise = useEnterpriseContactSalesCtaEligibility();

	// Mutually exclusive in practice, so the order here is not a tie-break so
	// much as a defensive one: if GUPS ever returned a profile satisfying both,
	// attributing the render to the experiment arm keeps the experiment's own
	// events honest rather than silently counting them as SMB.
	const leadSegment: SalesLeadSegment | undefined = enterprise.isEligible
		? 'enterprise'
		: smb.isEligible
			? 'smb'
			: undefined;

	return {
		isEligible: smb.isEligible || enterprise.isEligible,
		isLoading: smb.isLoading || enterprise.isLoading,
		leadSegment,
	};
};

export const ContactSalesCta = ({ isFullWidth }: { isFullWidth?: boolean }) => {
	const { isEligible, isLoading, leadSegment } = useAnyContactSalesCtaEligibility();
	const productKey = useMemo<ProductKeyTypes>(() => {
		const pathname = globalThis.location?.pathname ?? '';
		if (pathname.includes('/software/jira')) return 'jira';
		if (pathname.includes('/software/confluence')) return 'confluence';
		if (pathname.includes('/enterprise/contact')) return 'enterpriseContactSales';
		if (pathname === '/') return 'home';
		return undefined;
	}, []);

	// `leadSegment` is defined exactly when one of the two paths is eligible, so
	// testing it also narrows the type for the renderer below.
	if (isLoading || !isEligible || !leadSegment) {
		return;
	}

	return (
		<CtaButtonRenderer
			isFullWidth={isFullWidth}
			productKey={productKey}
			leadSegment={leadSegment}
		/>
	);
};

const CtaButtonRenderer = ({
	isFullWidth,
	productKey,
	leadSegment,
}: {
	isFullWidth?: boolean;
	productKey: ProductKeyTypes;
	leadSegment: SalesLeadSegment;
}) => {
	const analyticsClient = useAnalyticsClient();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const localizedHref = useLocalizedHref('/enterprise/contact?formType=product-features');

	const hasFiredRef = useRef(false);

	useEffect(() => {
		if (analyticsClient && !hasFiredRef.current) {
			hasFiredRef.current = true;
			createAnalyticsEvent({
				eventType: 'ui',
				action: 'viewed',
				actionSubject: 'button',
				actionSubjectId: 'contactSalesCta',
				source: 'globalNav',
				attributes: {
					isMobile: !!isFullWidth,
					productKey: productKey,
					leadSegment,
				},
			}).fire();
		}
	}, [analyticsClient, createAnalyticsEvent, isFullWidth]);

	useEffect(() => {
		Tracking.autoTrackLinks(true);
	}, []);

	return (
		<Button
			variant="primary_alt"
			css={isFullWidth && fullWidthStyles}
			href={localizedHref}
			dataEventProps={{
				'data-product-key': productKey,
				'data-event': 'clicked',
				'data-event-component': 'linkButton',
				'data-event-container': 'navbar',
				'data-label-english': 'Contact us',
				'data-action-subject-id': CTA_CLICK_ACTION_SUBJECT_ID[leadSegment],
			}}
		>
			<FormattedMessage id="global-nav.contact-sales-cta.label" defaultMessage="Contact us" />
		</Button>
	);
};
